// src/pages/Packages.jsx
import React from "react";

export default function Packages() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-14">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
          Dealer Sign Up
        </h1>
        <p className="mt-3 text-sm sm:text-base text-neutral-400">
          Join the UL Wraps dealer network. Get early access to new colors, real dealer pricing,
          and be the shop we send customers to when they want it done right.
        </p>
      </div>

      {/* Benefits */}
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        <DealerBenefit
          title="Dealer Pricing"
          points={[
            "Tiered pricing based on annual volume",
            "Access to dealer-only color drops & bundles",
            "Priority inventory allocation on limited runs",
          ]}
        />
        <DealerBenefit
          title="Leads & Visibility"
          points={[
            "Featured on our Preferred Installer map",
            "Customer leads routed directly to your shop",
            "Permission to use UL Wraps branding in your marketing",
          ]}
        />
        <DealerBenefit
          title="Support & Launches"
          points={[
            "Early access to new colors before public release",
            "Launch kits: photos, social assets, sample cards",
            "Direct line to our team for film & install questions",
          ]}
        />
      </div>

      {/* Program details */}
      <div className="mt-10 grid gap-10 md:grid-cols-2">
        <div className="space-y-4 text-sm text-neutral-300">
          <h2 className="text-xl font-semibold text-white">
            What we look for in a UL Wraps dealer
          </h2>
          <p>
            We’re not trying to be on every corner. We want shops that actually care about fitment,
            finish and customer experience. If you’re already doing clean work and want a brand
            that backs you up, you’re our people.
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>Established Wrap / PPF studios</li>
            <li>Clean shop space and legit install standards</li>
            <li>Strong reputation or clear plan to build one</li>
            <li>Willing to post and tag UL Wraps installs on social</li>
          </ul>

          <p className="pt-2 text-neutral-400 text-xs">
            *Acceptance is not guaranteed. We limit dealers per area to protect your territory and
            keep pricing from turning into a race to the bottom.
          </p>
        </div>

        {/* Dealer form */}
        <div className="rounded-2xl border border-neutral-800 bg-neutral-950/80 p-6">
          <h2 className="text-lg font-semibold text-white mb-3">
            Dealer Application
          </h2>
          <p className="text-xs text-neutral-400 mb-4">
            Fill this out and we’ll reach out with next steps, current dealer pricing
            and map listing details.
          </p>

          <form
            action="https://formspree.io/f/mjknowrk"
            method="POST"
            className="space-y-3 text-sm"
          >
            {/* anti-bot */}
            <input type="text" name="_gotcha" className="hidden" tabIndex={-1} autoComplete="off" />
            <input type="hidden" name="_subject" value="Dealer Sign Up — UL Wraps" />

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Full Name *">
                <input
                  name="fullName"
                  required
                  className="w-full rounded-xl border border-neutral-700 bg-black px-3 py-2 text-sm text-white"
                />
              </Field>
              <Field label="Business Name *">
                <input
                  name="businessName"
                  required
                  className="w-full rounded-xl border border-neutral-700 bg-black px-3 py-2 text-sm text-white"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Email *">
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full rounded-xl border border-neutral-700 bg-black px-3 py-2 text-sm text-white"
                />
              </Field>
              <Field label="Phone*">
                <input
                  name="phone"
                  required
                  className="w-full rounded-xl border border-neutral-700 bg-black px-3 py-2 text-sm text-white"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="City / State / Country *">
                <input
                  name="location"
                  required
                  className="w-full rounded-xl border border-neutral-700 bg-black px-3 py-2 text-sm text-white"
                />
              </Field>
              <Field label="Website or Booking Link">
                <input
                  name="website"
                  className="w-full rounded-xl border border-neutral-700 bg-black px-3 py-2 text-sm text-white"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Instagram Handle">
                <input
                  name="instagram"
                  placeholder="@yourshop"
                  className="w-full rounded-xl border border-neutral-700 bg-black px-3 py-2 text-sm text-white"
                />
              </Field>
              <Field label="Average Full Wraps / Month">
                <input
                  name="wrapVolume"
                  type="number"
                  min="0"
                  className="w-full rounded-xl border border-neutral-700 bg-black px-3 py-2 text-sm text-white"
                />
              </Field>
            </div>

            <Field label="What services do you offer?">
              <textarea
                name="services"
                rows={2}
                placeholder="Color change wraps, PPF, tint, detailing, etc."
                className="w-full rounded-xl border border-neutral-700 bg-black px-3 py-2 text-sm text-white"
              />
            </Field>

            <Field label="Why do you want to partner with UL Wraps?">
              <textarea
                name="why"
                rows={3}
                className="w-full rounded-xl border border-neutral-700 bg-black px-3 py-2 text-sm text-white"
              />
            </Field>

            <button
              type="submit"
              className="mt-2 inline-flex items-center justify-center rounded-xl bg-white text-black px-5 py-2.5 text-sm font-semibold hover:bg-neutral-200 transition"
            >
              Submit Application
            </button>

            <p className="text-[11px] text-neutral-500 mt-2">
              By submitting, you agree we may contact you about the dealer program. We don’t share your info.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function DealerBenefit({ title, points }) {
  return (
    <div className="rounded-2xl border border-neutral-800 bg-neutral-950/80 p-5">
      <h3 className="text-sm font-semibold text-white">{title}</h3>
      <ul className="mt-3 space-y-1 text-xs text-neutral-300 list-disc list-inside">
        {points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1 text-neutral-200">
        {label}
      </label>
      {children}
    </div>
  );
}
